// partners/adminView/pages/A6_AdminCommissions.jsx

import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  useGetAdminCommissionsQuery,
  useConfirmCommissionMutation,
  useCalculateCommissionsMutation,
  useConfirmCommissionsMutation,
} from "../../../apis/adminApiSlice";
import AdminLayout from "./A0_AdminLayout";
import { formatUSD } from "../formatMoney";
import { capturePartnerEvent } from "../../../utils/analytics";

const STATUS_BADGE = {
  pending:   "bg-yellow-500/20 text-yellow-400",
  confirmed: "bg-mnf-cyan/20 text-mnf-cyan",
  paid:      "bg-mnf-success/20 text-mnf-success",
  reversed:  "bg-mnf-error/20 text-mnf-error",
};

const TYPE_BADGE = {
  ecosystem:           "bg-mnf-cyan/15 text-mnf-cyan",
  creative_ambassador: "bg-mnf-purple/15 text-purple-400",
};

function SummaryCard({ title, subtitle, stats, rateLabel }) {
  return (
    <div className="bg-mnf-dark-2 border border-white/[0.08] rounded-mnf-lg p-5">
      <div className="text-xs text-mnf-text-muted uppercase tracking-wider font-poppins mb-1">
        {title}
      </div>
      <div className="text-xs text-mnf-text-muted mb-3">{subtitle}</div>
      {rateLabel && (
        <div className="text-[11px] text-mnf-cyan mb-2 font-poppins">{rateLabel}</div>
      )}
      <div className="font-poppins text-2xl font-bold text-mnf-text-primary mb-1">
        {formatUSD(stats?.commission_total)}
      </div>
      <div className="text-xs text-mnf-text-muted">
        {stats?.count ?? 0} line(s) · {formatUSD(stats?.payment_total)} referral payments (USD)
      </div>
    </div>
  );
}

export default function AdminCommissions() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [feedback, setFeedback] = useState("");

  const { data, isLoading, refetch } = useGetAdminCommissionsQuery({
    month,
    year,
    status: statusFilter,
    partner_type: typeFilter,
  });
  const [confirmCommission, { isLoading: confirming }] = useConfirmCommissionMutation();
  const [calculateCommissions, { isLoading: calculating }] = useCalculateCommissionsMutation();
  const [confirmCommissions, { isLoading: confirmingAll }] = useConfirmCommissionsMutation();

  const commissions = data?.results || [];
  const summary = data?.summary;
  const viewedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && !viewedRef.current) {
      viewedRef.current = true;
      capturePartnerEvent("partner_admin_commissions_viewed");
    }
  }, [isLoading]);

  const trackCommissionFilter = (overrides = {}) => {
    capturePartnerEvent("partner_admin_commissions_filtered", {
      month: overrides.month ?? month,
      year: overrides.year ?? year,
      status: overrides.status ?? statusFilter,
      partner_type: overrides.partner_type ?? typeFilter,
    });
  };

  const handleCalculate = async () => {
    if (
      !window.confirm(
        `Calculate commissions for ${month}/${year}?\n\n` +
          "Uses each partner's converted referrals and their track rate " +
          "(Ecosystem 30%, Creative Ambassador 25%) on successful payments in that month."
      )
    ) {
      return;
    }
    try {
      const res = await calculateCommissions({ month, year }).unwrap();
      const parts = [res.message];
      if (res.summary_message) parts.push(res.summary_message);
      setFeedback(parts.join(" "));
      capturePartnerEvent("partner_admin_commissions_calculated", { month, year, success: true });
      refetch();
    } catch (e) {
      capturePartnerEvent("partner_admin_commissions_calculated", { month, year, success: false });
      setFeedback(`Calculate failed: ${e?.data?.error || e?.data?.detail || "Unknown error"}.`);
    }
  };

  const handleConfirmAll = async () => {
    if (
      !window.confirm(
        `Confirm all pending commissions for ${month}/${year}?\n\n` +
          "Re-checks CentralPayment (success minus refunds) and marks zero-net rows as reversed."
      )
    ) {
      return;
    }
    try {
      const res = await confirmCommissions({ month, year }).unwrap();
      setFeedback(res.message);
      capturePartnerEvent("partner_admin_commissions_confirmed_all", { month, year, success: true });
      refetch();
    } catch (e) {
      capturePartnerEvent("partner_admin_commissions_confirmed_all", { month, year, success: false });
      setFeedback(`Confirm failed: ${e?.data?.error || "Unknown error"}.`);
    }
  };

  const handleConfirm = async (commId) => {
    try {
      await confirmCommission(commId).unwrap();
      setFeedback("Commission confirmed.");
      capturePartnerEvent("partner_admin_commission_confirmed", { commission_id: commId });
      refetch();
    } catch {
      setFeedback("Confirm failed.");
    }
  };

  const typeLabel = (t) =>
    t === "creative_ambassador" ? "Ambassador" : t === "ecosystem" ? "Ecosystem" : "—";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
        <div>
          <h1 className="font-poppins text-2xl font-bold">Commissions</h1>
          <p className="text-sm text-mnf-text-muted mt-1 max-w-2xl">
            One row per partner referral per month. Amounts come from successful payments
            on converted referrals, multiplied by that partner&apos;s commission rate.
          </p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <select
            value={month}
            onChange={(e) => {
              const val = Number(e.target.value);
              setMonth(val);
              trackCommissionFilter({ month: val });
            }}
            className="bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
          >
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(2000, i).toLocaleString("en-IN", { month: "long" })}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={year}
            onChange={(e) => {
              const val = Number(e.target.value);
              setYear(val);
              trackCommissionFilter({ year: val });
            }}
            min="2024"
            max="2030"
            className="w-24 bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
          />
          <button
            onClick={handleCalculate}
            disabled={calculating}
            className="text-xs font-poppins font-semibold px-4 py-2 rounded-mnf-pill border border-mnf-cyan/30 text-mnf-cyan hover:bg-mnf-cyan/10 disabled:opacity-50 transition-colors"
          >
            {calculating ? "Calculating…" : "1. Calculate"}
          </button>
          <button
            onClick={handleConfirmAll}
            disabled={confirmingAll}
            className="text-xs font-poppins font-semibold px-4 py-2 rounded-mnf-pill border border-white/20 text-mnf-text-secondary hover:border-mnf-cyan/40 disabled:opacity-50 transition-colors"
          >
            {confirmingAll ? "Confirming…" : "2. Confirm all"}
          </button>
          <Link
            to="/partners/admin/payouts"
            className="text-xs font-poppins font-semibold px-4 py-2 rounded-mnf-pill bg-mnf-gradient text-mnf-btn-text"
          >
            3. Pay partners →
          </Link>
        </div>
      </div>

      <p className="text-xs text-mnf-text-muted mb-4 max-w-3xl">
        Monthly flow: <strong className="text-mnf-text-secondary">Calculate</strong> commission rows from
        payments → <strong className="text-mnf-text-secondary">Confirm</strong> (or confirm one row) → open{" "}
        <strong className="text-mnf-text-secondary">Payouts</strong> to prepare TDS and send money via RazorpayX.
      </p>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <SummaryCard
            title="Film Ecosystem"
            subtitle="Production houses, schools, agencies"
            rateLabel="30% recurring on referral payments"
            stats={summary.ecosystem}
          />
          <SummaryCard
            title="Creative Ambassador"
            subtitle="Creators & influencers"
            rateLabel="25% recurring on referral payments"
            stats={summary.creative_ambassador}
          />
          <SummaryCard
            title="All partners"
            subtitle={`${month}/${year} (filtered)`}
            stats={summary.total}
          />
        </div>
      )}

      <div className="flex gap-3 mb-4 flex-wrap">
        <select
          value={typeFilter}
          onChange={(e) => {
            const val = e.target.value;
            setTypeFilter(val);
            trackCommissionFilter({ partner_type: val });
          }}
          className="bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
        >
          <option value="">All partner types</option>
          <option value="ecosystem">Ecosystem (30%)</option>
          <option value="creative_ambassador">Ambassador (25%)</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            const val = e.target.value;
            setStatusFilter(val);
            trackCommissionFilter({ status: val });
          }}
          className="bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="paid">Paid</option>
          <option value="reversed">Reversed</option>
        </select>
      </div>

      {feedback && <div className="mb-4 text-sm text-mnf-cyan">{feedback}</div>}

      {isLoading ? (
        <div className="text-mnf-text-muted text-sm">Loading...</div>
      ) : (
        <div className="bg-mnf-dark-2 border border-white/[0.08] rounded-mnf-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]">
            <thead className="bg-mnf-dark-3 text-mnf-text-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left">Partner</th>
                <th className="px-5 py-3 text-left">Type</th>
                <th className="px-5 py-3 text-left">Referral</th>
                <th className="px-5 py-3 text-left">Referral payment</th>
                <th className="px-5 py-3 text-left">Commission</th>
                <th className="px-5 py-3 text-left">Rate</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Period</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {commissions.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-8 text-center text-mnf-text-muted">
                    No commissions for {month}/{year}.
                    {typeFilter || statusFilter ? " Try clearing filters." : ""}{" "}
                    Click &quot;Calculate commissions&quot; to build rows from converted referrals.
                  </td>
                </tr>
              )}
              {commissions.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-white/[0.04] hover:bg-mnf-dark-3 transition-colors"
                >
                  <td className="px-5 py-3">
                    <div className="font-semibold">{c.partner_name || "—"}</div>
                    <div className="text-xs font-mono text-mnf-text-muted">{c.partner_code}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-xs font-poppins font-semibold px-2.5 py-1 rounded-full ${
                        TYPE_BADGE[c.partner_type] || "bg-white/10 text-white"
                      }`}
                    >
                      {typeLabel(c.partner_type)}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-xs">{c.referral_email || "—"}</div>
                    <div className="text-[11px] text-mnf-text-muted">
                      {c.plan_name || c.referral_status || "—"}
                    </div>
                  </td>
                  <td className="px-5 py-3">{formatUSD(c.payment_amount)}</td>
                  <td className="px-5 py-3 text-mnf-cyan font-semibold">
                    {formatUSD(c.commission_amount)}
                  </td>
                  <td className="px-5 py-3 text-mnf-text-muted">{c.commission_rate}%</td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-xs font-poppins font-semibold px-2.5 py-1 rounded-full ${
                        STATUS_BADGE[c.status] || "bg-white/10 text-white"
                      }`}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-mnf-text-muted whitespace-nowrap">
                    {c.period_start} → {c.period_end}
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    {c.status === "pending" && (
                      <button
                        onClick={() => handleConfirm(c.id)}
                        disabled={confirming}
                        className="text-xs text-mnf-cyan hover:underline disabled:opacity-50 mr-3"
                      >
                        Confirm
                      </button>
                    )}
                    {c.partner_id && (
                      <Link
                        to={`/partners/admin/partners/${c.partner_id}`}
                        className="text-xs text-mnf-text-muted hover:text-mnf-cyan"
                      >
                        Partner →
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
